import { debounce, reduce, omitBy, isNil } from 'lodash'

import LocationFilter from './location-filter'
import AgesFilter from './ages-filter'
import CatFilter from './cat-filter'
import MunicipalityFilter from './municipality-filter'
import AttrsFilter from './attrs-filter'
import DateFilter from './date-filter'
import CollectionFilter from './collection-filter'
import GMaps from './gmaps'
import { saveUserSearches } from '../helpers/api'
import { COLLECTIONS, getPage } from '../helpers/constants'
import CollectionAlternateFilter from './collection-alternate-filter'

export default class Search {
  /**
   * @param {object} options
   * @param {boolean?} options.auto - Determines whether the filter state change will trigger an update
   * @param {array} options.filters - Determines which filters to include
   * @param {array} options.collection - Determines which collection to filter
   * @param {boolean?} options.alternateCollectionFilter - Determines which collection filter to use
   */
  constructor (options) {
    const DEBOUNCE_TIME = 750
    this._searchBtn = document.getElementById('search-btn')
    this._searchInput = document.getElementById('search-box-input')
    this.debouncedClean = debounce(this.clean, DEBOUNCE_TIME)
    this.debouncedApply = debounce(this.apply, DEBOUNCE_TIME)

    this.collection = options.collection || COLLECTIONS.PLACES

    this.filter = {
      search: undefined,
      latitude: undefined,
      longitude: undefined
    }
    this.queryParams = undefined

    this.filterObjects = {
      locationFilter: undefined,
      agesFilter: undefined,
      catFilter: undefined,
      municipalityFilter: undefined,
      attrsFilter: undefined,
      dateFilter: undefined,
      collectionFilter: undefined
    }

    // LOCATION FILTER
    if (options.filters.includes('location')) {
      this.filterObjects.locationFilter = new LocationFilter()

      document.addEventListener(LocationFilter.EVENT.CHANGE, () => {
        this.debouncedApply()
      })
    }

    // AGES FILTER
    if (options.filters.includes('ages')) {
      this.filterObjects.agesFilter = new AgesFilter()

      if (options?.auto) {
        document.addEventListener(AgesFilter.EVENT.CHANGE, () => this.debouncedApply())
      } else {
        document.addEventListener(AgesFilter.EVENT.OPEN, (event) => this._handleOpenFilter(event.type))
      }
    }

    // CATEGORIES FILTER
    if (options.filters.includes('categories')) {
      this.filterObjects.catFilter = new CatFilter()

      if (options?.auto) {
        document.addEventListener(CatFilter.EVENT.CHANGE, () => this.debouncedApply())
      } else {
        document.addEventListener(CatFilter.EVENT.OPEN, (event) => this._handleOpenFilter(event.type))
      }
    }

    // MUNICIPALITY FILTER
    if (options.filters.includes('municipality')) {
      this.filterObjects.municipalityFilter = new MunicipalityFilter()

      if (!options?.auto) {
        document.addEventListener(MunicipalityFilter.EVENT.OPEN, (event) => this._handleOpenFilter(event.type))
      }
    }

    // ATTRIBUTES FILTER
    if (options.filters.includes('attributes')) {
      this.filterObjects.attrsFilter = new AttrsFilter({ collection: this.collection })

      if (options?.auto) {
        document.addEventListener(AttrsFilter.EVENT.CHANGE, () => this.debouncedApply())
      }
    }

    // DATE FILTER
    if (options.filters.includes('date')) {
      this.filterObjects.dateFilter = new DateFilter()

      if (options?.auto) {
        document.addEventListener(DateFilter.EVENT.CHANGE, () => this.debouncedApply())
      }
    }

    // MAP EVENTS
    if (options.filters.includes('map')) {
      document.addEventListener(GMaps.EVENT.LOCATION_CHANGE, (event) => {
        this.filter.latitude = event.detail.latitude
        this.filter.longitude = event.detail.longitude
        if (event.detail.apply) this.debouncedApply()
      })
    }

    // UPDATE COLLECTION
    if (options.filters.includes('collection')) {
      if (!options.alternateCollectionFilter) {
        this.filterObjects.collectionFilter = new CollectionFilter()

        document.addEventListener(CollectionFilter.EVENT.CHANGE, (event) => {
          this.collection = event.detail.collection
        })
      } else {
        this.filterObjects.collectionFilter = new CollectionAlternateFilter()

        document.addEventListener(CollectionFilter.EVENT.CHANGE, (event) => {
          this.collection = event.detail.collection
        })
      }
    }

    if (!this._searchBtn) { throw new Error('#search-btn element not found') }
    if (!this._searchInput) { throw new Error('#search-box-input element not found') }

    this._searchBtn.addEventListener('click', () => this._onSearch())
    this._searchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this._onSearch()
      } else if (this.filterObjects.locationFilter?.hasBeenAutoSet() &&
                  this.filterObjects.locationFilter?.filterIsSet()) {
        this.filterObjects.locationFilter.off({ auto: false })
      }
    })
  }

  static get EVENT () {
    return {
      UPDATE: 'poin:searchFilters:update'
    }
  }

  get allFilters () {
    return reduce(this.filterObjects, (acc, value) => {
      if (value?.filterIsSet()) {
        return Object.assign(acc, value.filter)
      } else {
        return acc
      }
    }, this.filter.search || this.filter.latitude ? omitBy(this.filter, isNil) : {})
  }

  async init () {
    this.queryParams = new URLSearchParams(window.location.search)
    this.filter.search = this.queryParams.get('search')

    await this.filterObjects.locationFilter?.init()
    await this.filterObjects.agesFilter?.init()
    await this.filterObjects.catFilter?.init()
    await this.filterObjects.municipalityFilter?.init()
    await this.filterObjects.attrsFilter?.init()
    await this.filterObjects.dateFilter?.init()
    await this.filterObjects.collectionFilter?.init()
  }

  initialLocationCheck () {
    this.filterObjects.locationFilter.on({ auto: true })
  }

  // This function will only be called if both filters are used. Currently we are simplifying
  // and that won't be the case. We keep it for the future
  checkLocationAndMunicipalityCoherence () {
    if (this.filterObjects.municipalityFilter.filterIsSet()) {
      this.filterObjects.municipalityFilter.enable()
      this.filterObjects.locationFilter.disable()
    } else if (this.filterObjects.locationFilter.filterIsSet()) {
      this.filterObjects.locationFilter.enable()
      this.filterObjects.municipalityFilter.disable()
    } else {
      this.filterObjects.locationFilter.enable()
      this.filterObjects.municipalityFilter.enable()
    }
  }

  clean () {
    this.filter.search = undefined
    this.filter.latitude = undefined
    this.filter.longitude = undefined
    this.filterObjects.locationFilter?.off()
    this.filterObjects.agesFilter?.clean()
    this.filterObjects.catFilter?.clean()
    this.filterObjects.municipalityFilter?.clean()
    this.filterObjects.attrsFilter?.clean()
    this.filterObjects.dateFilter?.clean()

    this.apply()
  }

  apply () {
    this._searchInput.value = this.filter.search || this._searchInput.value || ''
    this._searchInput.dispatchEvent(new CustomEvent(Search.EVENT.UPDATE, {
      bubbles: true,
      detail: this.allFilters
    }))
  }

  _onSearch () {
    // Get search text if not empty
    const searchText = this._searchInput.value
    if (searchText !== '') this.filter.search = searchText
    else this.filter.search = undefined

    // Save the search text and filters applied in DB
    saveUserSearches(searchText, 'home', this.allFilters)
    // Compose URL with query params (from filters) and redirect to results page
    const queryParams = new URLSearchParams(this.allFilters).toString()
    window.location.href = `/${getPage(this.collection)}?${queryParams}`
  }

  _handleOpenFilter (type) {
    switch (type) {
      case AgesFilter.EVENT.OPEN:
        this.filterObjects.catFilter?.close()
        this.filterObjects.municipalityFilter?.forceClose()
        break
      case CatFilter.EVENT.OPEN:
        this.filterObjects.agesFilter?.close()
        this.filterObjects.municipalityFilter?.forceClose()
        break
      case MunicipalityFilter.EVENT.OPEN:
        this.filterObjects.agesFilter?.close()
        this.filterObjects.catFilter?.close()
    }
  }
}
